import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, CreditCard, PiggyBank, AlertCircle, Coins, PlusCircle, MinusCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета вклада
 */
const ResultsOverview = ({ results, formData, currencyOptions }) => {
  if (!results) return null;

  // Получаем символ валюты
  const currency = currencyOptions.find(c => c.id === formData.currency);
  const currencySymbol = currency ? currency.symbol : '';
  
  // Расчет процента прироста от начальной суммы
  const growthPercent = ((results.finalAmount - results.initialAmount) / results.initialAmount) * 100;

  // Функция форматирования денежных значений с символом валюты
  const formatMoney = (value) => {
    return `${formatNumber(value, 2, '.', ' ')} ${currencySymbol}`;
  };
  
  <ResultItem 
    label="Начальная сумма вклада" 
    value={formatMoney(results.initialAmount)} 
    color="green" 
    icon={<CreditCard className="w-4 h-4"/>}
  />

  return (
    <ToolResult title="Результаты расчета вклада">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Итоговая сумма */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <Coins className="w-4 h-4"/>
            Итоговая сумма вклада
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatMoney(results.finalAmount)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Доход: {formatMoney(results.netGain)} (уже с учетом НДФЛ)
          </p>
        </div>

        {/* Прирост и эффективная ставка */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Прирост вклада
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              +{formatNumber(growthPercent, 2)}%
            </span>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              за {results.actualTermMonths} {formatMonthsLabel(results.actualTermMonths)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Эффективная годовая ставка: {formatNumber(results.effectiveRate, 2)}%
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальная сумма вклада" 
          value={formatMoney(results.initialAmount)} 
          color="green" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Процентная ставка" 
          value={`${formatNumber(formData.interestRate, 2)}% годовых`} 
          color="blue" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок вклада" 
          value={`${results.actualTermMonths} ${formatMonthsLabel(results.actualTermMonths)} (${Math.floor(results.actualTermMonths / 12)} ${formatYearsLabel(Math.floor(results.actualTermMonths / 12))} ${results.actualTermMonths % 12} мес.)`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Капитализация" 
          value={getCapitalizationName(results.capitalization)} 
        />

        <ResultItem 
          label="Начисленные проценты" 
          value={formatMoney(results.interestAmount)} 
          color="blue" 
        />
        
        {results.taxAmount > 0 && (
          <ResultItem 
            label="НДФЛ (13%)" 
            value={formatMoney(results.taxAmount)} 
            color="red" 
          />
        )}
        
        {results.totalContributions > 0 && (
          <ResultItem 
            label="Сумма пополнений" 
            value={formatMoney(results.totalContributions)} 
            color="green" 
            icon={<PlusCircle className="w-4 h-4"/>} 
          />
        )}
        
        {results.totalWithdrawals > 0 && (
          <ResultItem 
            label="Сумма снятий" 
            value={formatMoney(results.totalWithdrawals)} 
            color="orange" 
            icon={<MinusCircle className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Чистый доход" 
          value={formatMoney(results.netGain)} 
          color="green" 
          icon={<PiggyBank className="w-4 h-4"/>} 
        />

        {/* Информация об эффективной ставке */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Эффективная процентная ставка</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Эффективная ставка = <span className="font-medium text-blue-600 dark:text-blue-400">{formatNumber(results.effectiveRate, 2)}% годовых</span>. 
                Это реальный процент доходности вклада с учетом всех дополнительных условий (капитализация, пополнения, снятия).
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для получения названия типа капитализации
const getCapitalizationName = (capitalization) => {
  const types = {
    none: 'Без капитализации',
    daily: 'Ежедневная',
    monthly: 'Ежемесячная',
    quarterly: 'Ежеквартальная',
    annually: 'Ежегодная'
  };
  
  return types[capitalization] || capitalization;
};

// Вспомогательная функция для склонения слова "месяц" в зависимости от числа
const formatMonthsLabel = (months) => {
  const lastDigit = months % 10;
  const lastTwoDigits = months % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'месяцев';
  if (lastDigit === 1) return 'месяц';
  if (lastDigit >= 2 && lastDigit <= 4) return 'месяца';
  return 'месяцев';
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;